import type { TFunction } from 'i18next';
import { alertGroupOf, type IAlert } from '@d-orca/shared/alert-kinds';
import type { Feature } from '@d-orca/shared/permissions';
import { wordAlert } from '@d-orca/shared/client/notifications';
import type { INotificationItem } from '@interfaces/INotificationItem';
import { AlertVisual } from './AlertVisual/AlertVisual';

/** Where an alert opens in D-ORCA, if this person may go there (alerts spec, D4). */
export function alertRoute(siteId: string, alert: IAlert, can: (feature: Feature) => boolean): string | undefined {
  const site = encodeURIComponent(siteId);
  switch (alert.kind) {
    case 'safety.distress':
    case 'safety.restrictedArea':
      return can('safety.view') ? `/monitor/${site}` : undefined;
    case 'reports.dailyOverview':
      return undefined; // built on demand — see `resolveTo`
    case 'reports.tagHealth':
      return can('admin') ? `/admin/${site}?tab=tags` : undefined;
    case 'activities.planUpdated':
    case 'activities.statusUpdated':
    case 'issues.createdSummary':
      return `/activities/${site}`;
    case 'issues.commented':
    case 'issues.assigned':
    case 'issues.statusChanged':
      return alert.details
        ? `/activities/${site}?openIssue=${encodeURIComponent(alert.details.issueId)}`
        : `/activities/${site}`;
  }
}

export interface IAlertItemContext {
  siteId: string;
  t: TFunction;
  can: (feature: Feature) => boolean;
  /** Builds the Project Overview of the alert's day and answers its route —
   *  absent until the site's zone, which names that day, is known. */
  buildDailyOverview?: (alert: IAlert) => Promise<string | null>;
  markViewed: (alertId: string) => void;
  dismiss: (alertId: string) => void;
}

/** An alert drawn for the bell. */
export function alertItem(alert: IAlert, ctx: IAlertItemContext): INotificationItem {
  const { text, detail } = wordAlert(ctx.t, alert);
  const { buildDailyOverview } = ctx;
  const daily = alert.kind === 'reports.dailyOverview' && ctx.can('analytics.view') && buildDailyOverview;
  return {
    id: `alert:${alert.id}`,
    visual: <AlertVisual group={alertGroupOf(alert.kind)} />,
    text,
    ...(detail && { detail }),
    at: alert.at,
    to: alertRoute(ctx.siteId, alert, ctx.can),
    ...(daily && { resolveTo: () => daily(alert) }),
    unread: !alert.viewed,
    onOpened: () => {
      if (!alert.viewed) ctx.markViewed(alert.id);
    },
    onDismiss: () => ctx.dismiss(alert.id),
  };
}
